/**
 * /architect — Architect Agent home.
 *
 * Two sections, single column:
 *   1. "Running" — active decompositions (status=pending|running) shown
 *      as compact cards. Click → run detail.
 *   2. "History" — paginated table of past runs, sortable + filterable.
 *
 * Page header carries the "+ New task" CTA that opens /architect/dispatch.
 */
import type { ArchitectRunSummary } from '@chaos/types/architect';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import * as React from 'react';

import { RunHistoryTable } from '../../../components/architect/run-history-table';
import { Button } from '../../../components/ui/button';
import { architectApi, architectKeys } from '../../../lib/api/architect';
import { cn } from '../../../lib/utils';

function ArchitectHome(): React.JSX.Element {
  const runsQ = useQuery({
    queryKey: architectKeys.runs({ limit: 100 }),
    queryFn: () => architectApi.listRuns({ limit: 100 }),
    refetchInterval: (query) => {
      // Poll while at least one run is in flight; the WS handles per-run
      // detail, but the home page benefits from a coarse heartbeat.
      const data = query.state.data;
      if (!data) return false;
      const hasActive = data.runs.some((r) => r.status === 'running' || r.status === 'pending');
      return hasActive ? 5_000 : false;
    },
  });

  const runs = runsQ.data?.runs ?? [];
  const active = React.useMemo(
    () => runs.filter((r) => r.status === 'running' || r.status === 'pending'),
    [runs],
  );
  const finished = React.useMemo(
    () => runs.filter((r) => r.status !== 'running' && r.status !== 'pending'),
    [runs],
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center justify-between gap-4 px-6 pt-3 pb-2">
        <h1 className="truncate text-[15px] font-semibold tracking-tight text-foreground">
          Architect
        </h1>
        <Button asChild size="sm" variant="outline">
          <Link to="/architect/dispatch" className="gap-1.5">
            <Plus className="h-3.5 w-3.5" aria-hidden />
            New task
          </Link>
        </Button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-4">
        <section className="mb-8">
          <h2 className="mb-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Running
          </h2>
          {runsQ.isLoading ? (
            <p className="text-[12px] text-muted-foreground/70">Loading…</p>
          ) : active.length === 0 ? (
            <p className="text-[12px] text-muted-foreground/70">No active runs.</p>
          ) : (
            <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
              {active.map((run) => (
                <ActiveRunCard key={run.id} run={run} />
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="mb-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            History
          </h2>
          {runsQ.isLoading ? (
            <p className="text-[12px] text-muted-foreground/70">Loading…</p>
          ) : (
            <RunHistoryTable runs={finished} />
          )}
        </section>
      </div>
    </div>
  );
}

function ActiveRunCard({ run }: { run: ArchitectRunSummary }): React.JSX.Element {
  return (
    <Link
      to="/architect/run/$runId"
      params={{ runId: run.id }}
      className={cn(
        'group relative flex flex-col gap-1 rounded border border-border bg-background px-3 py-2.5',
        'transition-colors hover:border-foreground/30',
      )}
    >
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider',
            run.status === 'running'
              ? 'bg-foreground/8 text-foreground'
              : 'bg-muted text-muted-foreground',
          )}
        >
          {run.status}
        </span>
        <span className="text-[11px] text-muted-foreground">
          {new Date(run.startedAt).toLocaleTimeString()}
        </span>
      </div>
      <p className="line-clamp-2 text-[13px] text-foreground">{run.description}</p>
    </Link>
  );
}

export const Route = createFileRoute('/_authenticated/architect/')({
  component: ArchitectHome,
});
